import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Row, Column, Button, Container, Alert, Icon } from '@pella/sash-framework';
import { contentstackClient } from '../../../../utils/contentstack-client';
import { GET_ARTICLES_BY_TAXONOMY } from '../../../../enums/contentstack-graphql';
import { ItemList } from '../../../common/structured-data';
import { isValidArray } from '../../../utilities/validations';

import BaseCard from '../../../common/card';

import styles from './index.module.scss';

const LatestArticles = ({ articleTag, heading, categoryType, domain, className }) => {
	const filters = isValidArray(articleTag) ? articleTag?.map((filter) => ({ taxonomies: { filter_by: { term_in: filter?.split(', ') } } })) : [];
	const { data: articles, loading: initialLoad } = useQuery(GET_ARTICLES_BY_TAXONOMY, { client: contentstackClient, variables: { filters, limit: 7 }, skip: !isValidArray(articleTag) });

	const [skip, setSkip] = useState(7);
	const [articleData, setArticleData] = useState([]);
	const [error, setError] = useState({});

	const [loadArticles, { loading }] = useLazyQuery(GET_ARTICLES_BY_TAXONOMY, {
		client: contentstackClient,
		onCompleted: (data) => onLoadComplete(data?.all_articles?.items),
		onError: (error) => setError(error),
	});

	useEffect(() => {
		if (isValidArray(articles?.all_articles?.items)) {
			setArticleData(articles.all_articles.items);
		}
	}, [articles]);

	const onLoadComplete = (nextArticles) => {
		if (isValidArray(nextArticles)) {
			setArticleData([...articleData, ...nextArticles]);
			setSkip(skip + 7);
		}
	};

	if (!isValidArray(articleData)) return null;

	return (
		<div className={className || null}>
			<Container>
				{heading && <h4 className={`${categoryType === 'article' ? styles.latestArticle_Heading : styles.localProjects_Heading}`}>{heading.toUpperCase()}</h4>}

				{error?.message && (
					<Alert
						type='error'
						hideClose
					>
						{error.message}
					</Alert>
				)}

				<Row>
					{articleData.map((tips, index) => {
						const image = { image: tips.seo?.thumbnailConnection?.edges?.[0]?.node };

						return (
							<React.Fragment key={index}>
								{(index != 0 && index % 7 != 0 && (
									<Column
										className={styles.latestArticle_imageCard}
										xs={12}
										md={6}
										lg={4}
									>
										<BaseCard
											className={styles.latestImage}
											data={{
												ctas: [
													{
														link: { href: tips.url },
													},
												],
												image: image,
											}}
											params='?format=pjpg&auto=webp&height=400'
										>
											{tips.top_content?.heading && <h3>{tips.top_content.heading}</h3>}
											{tips.top_content?.subheading && <div dangerouslySetInnerHTML={{ __html: tips.top_content.subheading }} />}
											{!tips.top_content?.subheading && tips.seo?.description && <div dangerouslySetInnerHTML={{ __html: tips.seo.description }} />}
										</BaseCard>
									</Column>
								)) || (
									<div className={styles.latestArticle_HorizontalImage}>
										<BaseCard
											layout='horizontal'
											data={{
												ctas: [
													{
														link: { href: tips.url },
													},
												],
												image: image,
											}}
											params='?format=pjpg&auto=webp&height=450'
											className={styles.largeLatestImage}
										>
											{tips.top_content?.heading && <h3>{tips.top_content.heading}</h3>}
											{tips.top_content?.subheading && <div dangerouslySetInnerHTML={{ __html: tips.top_content.subheading }} />}
											{!tips.top_content?.subheading && tips.seo?.description && <div dangerouslySetInnerHTML={{ __html: tips.seo.description }} />}
										</BaseCard>
									</div>
								)}
							</React.Fragment>
						);
					})}
				</Row>

				{articles?.all_articles?.total > articleData?.length && (
					<div className={styles.latestArticle_ButtonWrapper}>
						<Button
							onClick={() => loadArticles({ variables: { filters, limit: 7, skip: skip } })}
							className={styles.latestArticle_Button}
						>
							Load More Articles
						</Button>
					</div>
				)}
			</Container>

			{(loading || initialLoad) && (
				<div className='my-10 text-center'>
					<Icon
						name='progress_activity'
						size={48}
						className='animate-spin text-gray-60'
					/>
				</div>
			)}

			{isValidArray(articleData) && (
				<ItemList
					items={articleData}
					domain={domain}
				/>
			)}
		</div>
	);
};

LatestArticles.propTypes = {
	articleTag: PropTypes.array,
	className: PropTypes.string,
	domain: PropTypes.string,
	heading: PropTypes.string,
	categoryType: PropTypes.string,
};

export default LatestArticles;
